import React, { useState, useEffect, Fragment } from 'react'
import { Formik } from 'formik'
import classnames from 'classnames'
import Form, {
  Row,
  Col,
  Label,
  Field,
  Button,
  ErrorMessage,
  Title,
  MaskedInput,
  Input
} from '../../../components/Form'
import ErrorModal from '../../../components/ErrorModal'
import { useToggle } from '../../../utils/hooks'
import { validationSchema, expirationDateMask } from './utils'
import Step from '../components/Step'
import CreditCardModal from './CreditCardModal'
import { pathOr } from 'ramda'

const PaymentDetails = props => {
  const {
    isActive,
    cards,
    selectedCard,
    changeSelectedCard,
    cardsLoading,
    cardsCached,
    getCards,
    createCard,
    createOrder,
    userInfo,
    history,
    initialCoupon,
    changeCoupon,
    applyCoupon,
    orderConfirmationLoading,
    couponLoading
  } = props

  const [showCreditCardModal, setShowCreditCardModal] = useState(false)
  const [coupon, setCoupon] = useState(initialCoupon)
  const [cardError, setCardError] = useState('')
  const [couponError, setCouponError] = useState('')
  const [useCoupon, toggleUseCoupon] = useToggle(!!initialCoupon)
  const [showErrorModal, toggleErrorModal] = useToggle(false)

  useEffect(() => {
    if (isActive && !cardsLoading && !cardsCached) {
      getCards()
    }
  }, [isActive])

  const toggleCreditCardModal = () => {
    setShowCreditCardModal(!showCreditCardModal)
  }

  const onCreateCard = ({ card, expiration, cvv }) => {
    const [month, year] = expiration.split('/')
    return createCard({
      firstname: pathOr('', ['firstname'], userInfo),
      lastname: pathOr('', ['lastname'], userInfo),
      month: +month,
      year: +year,
      code: cvv,
      card: card.trim().replace(/ /g, '')
    })
  }

  const onSubmit = (values, { setSubmitting }) => {
    onCreateCard(values)
      .then(({ success, error }) => {
        if (success) {
          setCardError('')
          return true
        } else {
          setCardError(error)
          return false
        }
      })
      .then(success => {
        if (success) {
          return createOrder().then(({ success, error }) => {
            if (success) {
              return true
            }

            toggleErrorModal()
            return false
          })
        }
        return false
      })
      .then(success => {
        setSubmitting(false)
        if (success) {
          history.push('/thankyou')
        }
      })
  }

  const onPlaceOrder = () => {
    createOrder().then(({ success, error }) => {
      if (success) {
        history.push('/thankyou')
      } else {
        toggleErrorModal()
      }
    })
  }

  const _applyCoupon_ = () => {
    if (useCoupon && coupon) {
      changeCoupon(coupon)

      applyCoupon().then(({ success, error }) => {
        if (success) {
          setCouponError('')
        } else {
          setCouponError(error)
        }
      })
    }
  }

  const hasNoCards = !cards.length

  const PromoFragment = (
    <Fragment>
      <Row>
        <Col>
          <Input
            checked={useCoupon}
            onClick={toggleUseCoupon}
            type="checkbox"
            label="Have a Promo Code?"
          />
        </Col>
      </Row>

      {useCoupon && (
        <div className="coupon_row">
          <Input value={coupon} onChange={e => setCoupon(e.target.value)} />
          <Button
            large
            disabled={
              !coupon ||
              (coupon && initialCoupon.toLowerCase() === coupon.toLowerCase())
            }
            onClick={_applyCoupon_}
            loading={couponLoading}>
            Apply
          </Button>
        </div>
      )}

      {!!couponError && (
        <Row>
          <Col>
            <div className="cookunity__form_error_message">{couponError}</div>
          </Col>
        </Row>
      )}
    </Fragment>
  )

  return (
    <Fragment>
      <ErrorModal
        isOpen={showErrorModal}
        title="Oh wait!"
        subtitle="Something went wrong with your transaction"
        onRequestClose={toggleErrorModal}
      />
      <CreditCardModal
        isOpen={showCreditCardModal}
        onRequestClose={toggleCreditCardModal}
        onCreateCard={onCreateCard}
      />
      <Step step={3} title="Payment" disabled={!isActive} active={isActive}>
        <div className="cookunity__payment_details_form">
          {hasNoCards ? (
            <Formik
              isInitialValid={false}
              initialValues={{
                card: '',
                expiration: '',
                cvv: ''
              }}
              validationSchema={validationSchema}
              onSubmit={onSubmit}>
              {({
                isSubmitting,
                isValid,
                values,
                handleChange,
                handleBlur
              }) => (
                <Form>
                  <Title>Credit Card</Title>
                  <div className="credit_card_form_grid">
                    <Col className="card_number">
                      <Label>Card Number</Label>
                      <Field
                        type="text"
                        name="card"
                        placeholder="0000 0000 0000 0000"
                      />
                      <ErrorMessage name="card" component="div" />
                    </Col>
                    <Col className="expiration">
                      <Label>Expiration</Label>
                      <MaskedInput
                        name="expiration"
                        placeholder="MM/YY"
                        value={values.expiration}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        mask={expirationDateMask}
                      />
                      <ErrorMessage name="expiration" component="div" />
                    </Col>
                    <Col className="cvv">
                      <Label>CVV</Label>
                      <Field type="text" name="cvv" placeholder="3 digits" />
                      <ErrorMessage name="cvv" component="div" />
                    </Col>
                  </div>

                  {!!cardError && (
                    <Row>
                      <Col>
                        <div className="cookunity__form_error_message">
                          {cardError}
                        </div>
                      </Col>
                    </Row>
                  )}

                  {PromoFragment}

                  <div className="next_step_row">
                    <Button
                      dark
                      large
                      type="submit"
                      loading={
                        orderConfirmationLoading || cardsLoading || isSubmitting
                      }
                      disabled={!isValid}>
                      Place Order
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          ) : (
            <Fragment>
              <div
                className={classnames('cards_container', {
                  loading: cardsLoading
                })}>
                {cards.map(card => (
                  <div className="credit_card" key={card.id}>
                    <div
                      className="radio_container"
                      onClick={() => changeSelectedCard(card)}>
                      <div
                        className={classnames('radio', {
                          selected: selectedCard.id === card.id
                        })}></div>
                    </div>
                    <div>
                      <p className="title">
                        {card.brand} •••• {card.last4}
                      </p>
                      <p className="due_date">
                        Due date: {card.expMonth}/{card.expYear}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="add_credit_card_row">
                <span onClick={toggleCreditCardModal}>
                  <i className="fas fa-plus" /> Add Credit Card
                </span>
              </div>

              {PromoFragment}

              <div className="next_step_row">
                <Button
                  dark
                  large
                  loading={orderConfirmationLoading}
                  onClick={onPlaceOrder}
                  disabled={!selectedCard || cardsLoading}>
                  Place Order
                </Button>
              </div>
            </Fragment>
          )}
        </div>
      </Step>
    </Fragment>
  )
}
export default PaymentDetails



// WEBPACK FOOTER //
// ./src/modules/Checkout/PaymentDetails/PaymentDetails.jsx